import type { TeamProfile } from '../../core/contracts';
import { h } from '../dom';
import type { ScreenCtx } from './context';
import { fixtureCard, letterBadge, screenHeader, statBlock, type ScreenInstance } from './common';
import { DAILY_RULES, dailyNumber, dailySeed, dailyStreak, dateKey } from '../../meta/daily';
import { formatCompact } from '../format';

const ord = (n: number) => `${n}${['th', 'st', 'nd', 'rd'][(n % 100 > 10 && n % 100 < 14) ? 0 : Math.min(n % 10, 4) % 4] ?? 'th'}`;

/** Side-by-side "broadcast brief": form, table position, manager, star player. */
export function matchBrief(home: TeamProfile, away: TeamProfile): HTMLElement {
  const col = (t: TeamProfile, side: 'home' | 'away') => {
    const star = [...t.players].filter((p) => p.position !== 'GK').sort((a, b) => b.rating - a.rating)[0];
    return h('div', { class: `sd-brief__col sd-brief__col--${side}`, style: `--tc:${t.primaryColor}` }, [
      h('div', { class: 'sd-brief__row' }, t.form.slice(-5).map((f) => h('span', { class: `sd-form sd-form--${f}`, text: f }))),
      h('div', { class: 'sd-brief__row sd-brief__big', text: ord(t.leaguePosition) }),
      h('div', { class: 'sd-brief__row sd-brief__small', text: t.manager }),
      h('div', { class: 'sd-brief__row sd-brief__small', text: star ? `#${star.number} ${star.name} · ${star.stats.seasonGoals} goal${star.stats.seasonGoals === 1 ? '' : 's'}` : '—' }),
    ]);
  };
  return h('div', { class: 'sd-brief' }, [
    col(home, 'home'),
    h('div', { class: 'sd-brief__mid' }, ['FORM', 'POSITION', 'MANAGER', 'ONE TO WATCH'].map((l) => h('span', { text: l }))),
    col(away, 'away'),
  ]);
}

export function dailyScreen(ctx: ScreenCtx): ScreenInstance {
  const now = ctx.now();
  const key = dateKey(now);
  const n = Math.max(1, dailyNumber(key));
  const cfg = ctx.deps.generateMatchConfig(dailySeed(key));
  const save = ctx.store.get();
  const best = save.daily[key];
  const streak = dailyStreak(save.daily, now);

  // last 7 days strip
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (6 - i));
    const k = dateKey(d);
    const b = save.daily[k];
    const num = dailyNumber(k);
    return h('div', { class: `sd-daystrip__day ${k === key ? 'is-today' : ''} ${num < 1 ? 'is-pre' : ''}` }, [
      h('span', { class: 'sd-daystrip__dow', text: d.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase() }),
      b ? letterBadge(b.letter, 'sd-letter--sm') : h('span', { class: 'sd-daystrip__none', text: num < 1 ? '·' : '–' }),
      h('span', { class: 'sd-daystrip__n', text: num >= 1 ? `#${num}` : '' }),
    ]);
  });

  const go = () => ctx.startDaily();
  const el = h('section', { class: 'sd-screen sd-page sd-daily' }, [
    screenHeader(`DAILY MATCH #${n}`, { kicker: now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase(), onBack: () => ctx.back() }),
    h('div', { class: 'sd-daily__body' }, [
      h('div', { class: 'sd-daily__main' }, [
        h('div', { class: 'sd-section-head' }, [h('span', { text: "TODAY'S FIXTURE — SAME FOR EVERYONE" })]),
        fixtureCard({ ...cfg, competition: `Daily Match #${n}` }, { big: true }),
        h('div', { class: 'sd-daily__rules' }, [
          h('span', { class: 'sd-chip', text: DAILY_RULES.difficulty.toUpperCase() }),
          h('span', { class: 'sd-chip', text: `${DAILY_RULES.halfLengthSeconds / 60} MIN HALVES` }),
          h('span', { class: 'sd-chip', text: 'BASE KIT · NO UNLOCKS' }),
          h('span', { class: 'sd-chip sd-chip--amber', text: '2.1M WATCHING' }),
        ]),
        matchBrief(cfg.home, cfg.away),
      ]),
      h('aside', { class: 'sd-daily__side sd-panel' }, [
        h('div', { class: 'sd-field__label', text: 'YOUR BEST TODAY' }),
        best
          ? h('div', { class: 'sd-daily__best' }, [
            letterBadge(best.letter, 'sd-letter--xl'),
            h('div', {}, [
              statBlock('RATING', String(best.rating)),
              statBlock('GOALS COVERED', `${best.goalsCovered}/${best.goals}`),
              statBlock('PEAK VIEWERS', formatCompact(best.peakViewers)),
              h('div', { class: 'sd-daily__attempts', text: `${best.attempts} ATTEMPT${best.attempts === 1 ? '' : 'S'}` }),
            ]),
          ])
          : h('div', { class: 'sd-daily__unplayed' }, [h('span', { class: 'sd-daily__unplayed-big', text: 'NOT YET ON AIR' }), h('span', { text: 'One fixture. Everyone gets the same match. Best result counts.' })]),
        h('div', { class: 'sd-field__label', text: `STREAK · ${streak} DAY${streak === 1 ? '' : 'S'}` }),
        h('div', { class: 'sd-daystrip' }, days),
        h('div', { class: 'sd-quick__spacer' }),
        h('button', { class: 'sd-btn sd-btn--primary sd-btn--golive', attrs: { type: 'button' }, on: { click: go } }, [h('span', { class: 'sd-golive__dot' }), best ? 'GO AGAIN' : 'GO LIVE', h('kbd', { text: 'ENTER' })]),
      ]),
    ]),
  ]);
  return {
    el,
    mounted() { (el.querySelector('.sd-btn--golive') as HTMLElement | null)?.focus({ preventScroll: true }); },
    onKey(e) { if (e.key === 'Enter' && document.activeElement === document.body) { go(); return true; } return false; },
  };
}
